import React, { useState, useEffect } from 'react';
import {
  Drawer, Box, Typography, Divider, TextField,
  Button, Chip, IconButton, CircularProgress,
} from '@mui/material';
import CloseIcon    from '@mui/icons-material/Close';
import AddIcon      from '@mui/icons-material/Add';
import DeleteIcon   from '@mui/icons-material/Delete';
import SaveIcon     from '@mui/icons-material/Save';
import ClearAllIcon from '@mui/icons-material/ClearAll';

const NAVY = '#12365a';

const STRATEGIC_OPTIONS = [
  { value: 'none',     label: 'None',     color: '#9CA3AF' },
  { value: 'low',      label: 'Low',      color: '#059669' },
  { value: 'medium',   label: 'Medium',   color: '#1769aa' },
  { value: 'high',     label: 'High',     color: '#b45309' },
  { value: 'critical', label: 'Critical', color: '#b91c1c' },
];

const PRESET_PROGRAMMES = ['White Glove', 'Test Case', 'At Risk', 'Strategic Partner', 'Early Adopter'];

const EMPTY_FORM = {
  strategicImportance: 'none',
  programmes: [],
  bonusPoints: '',
  bonusReason: '',
  attributes: [],
  notes: '',
};

export default function GuardrailAnnotationDrawer({ open, client, annotation, onSave, onDelete, onClose }) {
  const [form, setForm]               = useState(EMPTY_FORM);
  const [saving, setSaving]           = useState(false);
  const [deleting, setDeleting]       = useState(false);
  const [newProgramme, setNewProgramme] = useState('');
  const [error, setError]             = useState('');

  useEffect(() => {
    if (!open) return;
    if (annotation) {
      setForm({
        strategicImportance: annotation.strategicImportance || 'none',
        programmes:          annotation.programmes || [],
        bonusPoints:         annotation.bonusPoints != null ? String(annotation.bonusPoints) : '',
        bonusReason:         annotation.bonusReason || '',
        attributes:          annotation.attributes || [],
        notes:               annotation.notes || '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setError('');
    setNewProgramme('');
  }, [open, annotation]);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const addProgramme = (prog) => {
    const p = prog.trim();
    if (!p || form.programmes.includes(p)) { setNewProgramme(''); return; }
    set('programmes', [...form.programmes, p]);
    setNewProgramme('');
  };

  const addAttribute = () => set('attributes', [...form.attributes, { label: '', value: '' }]);

  const updateAttribute = (idx, field, val) =>
    set('attributes', form.attributes.map((a, i) => i === idx ? { ...a, [field]: val } : a));

  const removeAttribute = (idx) =>
    set('attributes', form.attributes.filter((_, i) => i !== idx));

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      await onSave(client.clientUrl, client.clientName, {
        strategicImportance: form.strategicImportance,
        programmes:          form.programmes,
        bonusPoints:         form.bonusPoints !== '' ? Number(form.bonusPoints) : 0,
        bonusReason:         form.bonusReason,
        attributes:          form.attributes.filter(a => a.label.trim()),
        notes:               form.notes,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!annotation?._id) { onClose(); return; }
    setDeleting(true);
    setError('');
    try {
      await onDelete(annotation._id);
      onClose();
    } catch (err) {
      setError(err.message || 'Clear failed');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{ sx: { width: 420, display: 'flex', flexDirection: 'column' } }}
    >
      {/* Header */}
      <Box sx={{ px: 2.5, py: 2, bgcolor: NAVY, color: '#fff', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexShrink: 0 }}>
        <Box sx={{ flex: 1, mr: 1 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
            {client?.clientName || '—'}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7, wordBreak: 'break-all', display: 'block' }}>
            {client?.clientUrl || ''}
          </Typography>
        </Box>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff', mt: -0.5, flexShrink: 0 }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Scrollable body */}
      <Box sx={{ flex: 1, overflowY: 'auto', px: 2.5, py: 2.5 }}>
        {error && (
          <Typography variant="caption" color="error" display="block" sx={{ mb: 1.5 }}>
            {error}
          </Typography>
        )}

        {/* Strategic Importance */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d788c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Strategic Importance
        </Typography>
        <Box sx={{ mt: 0.75, mb: 2.5, display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
          {STRATEGIC_OPTIONS.map(opt => {
            const active = form.strategicImportance === opt.value;
            return (
              <Chip
                key={opt.value}
                label={opt.label}
                size="small"
                onClick={() => set('strategicImportance', opt.value)}
                sx={{
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 28,
                  bgcolor: active ? opt.color : 'transparent',
                  color:   active ? '#fff' : opt.color,
                  border:  `1.5px solid ${opt.color}`,
                  '&:hover': { bgcolor: active ? opt.color : `${opt.color}18` },
                }}
              />
            );
          })}
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Programmes */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d788c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Programmes
        </Typography>
        <Box sx={{ mt: 0.75, mb: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap', minHeight: 24 }}>
          {form.programmes.length === 0 ? (
            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>None set</Typography>
          ) : form.programmes.map(p => (
            <Chip
              key={p}
              label={p}
              size="small"
              onDelete={() => set('programmes', form.programmes.filter(x => x !== p))}
              sx={{ fontWeight: 600, fontSize: '0.72rem', height: 24, bgcolor: `${NAVY}10`, color: NAVY, border: `1px solid ${NAVY}30` }}
            />
          ))}
        </Box>

        {/* Preset quick-add */}
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
          {PRESET_PROGRAMMES.filter(p => !form.programmes.includes(p)).map(p => (
            <Chip
              key={p}
              label={`+ ${p}`}
              size="small"
              onClick={() => addProgramme(p)}
              sx={{ cursor: 'pointer', fontSize: '0.7rem', height: 22, color: '#6d788c', border: '1px dashed #9CA3AF', bgcolor: 'transparent', '&:hover': { bgcolor: '#f3f4f6' } }}
            />
          ))}
        </Box>

        {/* Custom programme input */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
          <TextField
            size="small"
            placeholder="Add custom programme…"
            value={newProgramme}
            onChange={e => setNewProgramme(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addProgramme(newProgramme); } }}
            sx={{ flex: 1 }}
          />
          <Button
            size="small"
            variant="outlined"
            onClick={() => addProgramme(newProgramme)}
            disabled={!newProgramme.trim()}
            sx={{ borderColor: NAVY, color: NAVY, minWidth: 40, px: 1.5 }}
          >
            <AddIcon fontSize="small" />
          </Button>
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Bonus Points */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d788c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Bonus Points
        </Typography>
        <Box sx={{ mt: 0.75, mb: 2.5, display: 'flex', gap: 1 }}>
          <TextField
            size="small"
            type="number"
            label="Points"
            value={form.bonusPoints}
            onChange={e => set('bonusPoints', e.target.value)}
            sx={{ width: 120 }}
            inputProps={{ min: 0 }}
          />
          <TextField
            size="small"
            label="Reason"
            placeholder="Why are bonus points awarded?"
            value={form.bonusReason}
            onChange={e => set('bonusReason', e.target.value)}
            sx={{ flex: 1 }}
          />
        </Box>

        <Divider sx={{ mb: 2.5 }} />

        {/* Custom attributes */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d788c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            Custom Attributes
          </Typography>
          <Button
            size="small"
            startIcon={<AddIcon />}
            onClick={addAttribute}
            sx={{ color: NAVY, fontWeight: 600, fontSize: '0.72rem', textTransform: 'none', minWidth: 0, p: 0.5 }}
          >
            Add row
          </Button>
        </Box>
        {form.attributes.length === 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic', display: 'block', mb: 1.5 }}>No custom attributes</Typography>
        )}
        {form.attributes.map((attr, idx) => (
          <Box key={idx} sx={{ display: 'flex', gap: 1, mb: 0.75, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Key"
              value={attr.label}
              onChange={e => updateAttribute(idx, 'label', e.target.value)}
              sx={{ width: 130 }}
            />
            <TextField
              size="small"
              placeholder="Value"
              value={attr.value}
              onChange={e => updateAttribute(idx, 'value', e.target.value)}
              sx={{ flex: 1 }}
            />
            <IconButton size="small" onClick={() => removeAttribute(idx)} color="error" sx={{ flexShrink: 0 }}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        ))}

        <Divider sx={{ my: 2.5 }} />

        {/* Notes */}
        <Typography variant="caption" sx={{ fontWeight: 700, color: '#6d788c', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          Notes
        </Typography>
        <TextField
          size="small"
          multiline
          rows={4}
          fullWidth
          placeholder="Free text notes about this client…"
          value={form.notes}
          onChange={e => set('notes', e.target.value)}
          sx={{ mt: 0.75 }}
        />

        {annotation?.updatedByName && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
            Last updated by {annotation.updatedByName}
            {annotation.updatedAt ? ` · ${new Date(annotation.updatedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}` : ''}
          </Typography>
        )}
      </Box>

      {/* Footer */}
      <Box sx={{ px: 2.5, py: 2, borderTop: '1px solid rgba(0,0,0,0.1)', display: 'flex', gap: 1, flexShrink: 0 }}>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving || deleting}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />}
          sx={{ flex: 1, bgcolor: NAVY, '&:hover': { bgcolor: '#1a3a5c' }, textTransform: 'none', fontWeight: 600 }}
        >
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {annotation && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleDelete}
            disabled={saving || deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <ClearAllIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {deleting ? '…' : 'Clear'}
          </Button>
        )}
      </Box>
    </Drawer>
  );
}
